import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getInstagramComments,
  replyToInstagramComment,
  toggleInstagramAiCommentResponses,
} from '../../../hooks/comments';
import { getInstagramUserProfile } from '../../../hooks/instagram';
import {
  instagramAccountDetails,
  InstagramComment,
} from '../../../utils/types';
import { formatInstagramConversationTime, igAccent } from '../components/instagramInboxUi';

const InstagramCommentsPage = () => {
  const { orgId, instagramAccId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [comments, setComments] = useState<InstagramComment[]>([]);
  const [account, setAccount] = useState<instagramAccountDetails | null>(null);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [selectedComment, setSelectedComment] = useState<InstagramComment | null>(null);
  const [replyText, setReplyText] = useState('');
  const loaderRef = useRef<HTMLDivElement>(null);
  const aiInitialized = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchComments = async (url: string | null = null, reset = false) => {
    if (!orgId) return;
    if (loading) return;
    setLoading(true);

    try {
      const response = await getInstagramComments(orgId, instagramAccId, url || nextUrl);
      if (response.success) {
        const data = response.data;
        setComments((prev) =>
          reset ? data?.results || [] : [...prev, ...(data?.results || [])],
        );
        setNextUrl(data?.next || null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments(null, true);
  }, [orgId, instagramAccId]);

  useEffect(() => {
    const fetchAccount = async () => {
      if (!orgId) return;
      const response = await getInstagramUserProfile(orgId);
      if (response.success && Array.isArray(response.data) && response.data[0]) {
        setAccount(response.data[0]);
        setAiEnabled(Boolean(response.data[0].ai_comment_responses));
        aiInitialized.current = true;
      }
    };

    fetchAccount();
  }, [orgId]);

  useEffect(() => {
    if (!aiInitialized.current || !orgId || !instagramAccId) return;
    toggleInstagramAiCommentResponses(orgId, instagramAccId, aiEnabled).then(
      (response) => {
        if (!response.success) {
          toast({ title: 'Failed to update comment AI', status: 'error' });
        }
      },
    );
  }, [aiEnabled]);

  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && nextUrl) {
        fetchComments(nextUrl);
      }
    },
    [nextUrl, loading],
  );

  useEffect(() => {
    const node = loaderRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [observerCallback]);

  const sendReply = async () => {
    if (!selectedComment || !replyText.trim() || !orgId || !instagramAccId) return;

    const response = await replyToInstagramComment(
      orgId,
      instagramAccId,
      selectedComment.id,
      replyText.trim(),
    );

    if (response.success) {
      toast({
        title: 'Reply sent',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setReplyText('');
      onClose();
      fetchComments(null, true);
    } else {
      toast({
        title: 'Reply failed',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg="#F7F4F8" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <Box maxW="1200px" mx="auto">
        <Grid templateColumns={{ base: '1fr', lg: '340px 1fr' }} gap={6}>
          <Box
            bg="white"
            borderRadius="28px"
            border="1px solid"
            borderColor="rgba(15, 23, 42, 0.08)"
            p={6}
            h="fit-content"
          >
            <VStack align="stretch" spacing={5}>
              <Box>
                <Badge colorScheme="pink" borderRadius="full" px={3} py={1}>
                  Instagram comments
                </Badge>
                <Text mt={4} fontSize="xl" fontWeight="800" color="gray.900">
                  Comment workspace
                </Text>
                <Text mt={2} fontSize="sm" color="gray.500" lineHeight="1.8">
                  Review incoming Instagram comments, reply quickly, and manage
                  AI-assisted responses without leaving the connected account.
                </Text>
              </Box>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.900">
                  Connected account
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {account?.username ? `@${account.username}` : 'Instagram account'}
                </Text>
              </Box>

              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="sm" fontWeight="700" color="gray.900">
                    AI auto-response
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Toggle comment auto-replies for this account
                  </Text>
                </Box>
                <Switch
                  colorScheme="pink"
                  isChecked={aiEnabled}
                  onChange={(event) => setAiEnabled(event.target.checked)}
                />
              </Flex>

              <Button
                variant="outline"
                borderRadius="full"
                onClick={() =>
                  navigate(`/organization/${orgId}/inbox/instagram/conversations`)
                }
              >
                Back to inbox
              </Button>
            </VStack>
          </Box>

          <Box
            bg="white"
            borderRadius="28px"
            border="1px solid"
            borderColor="rgba(15, 23, 42, 0.08)"
            overflow="hidden"
          >
            <Flex
              px={6}
              py={5}
              justify="space-between"
              align="center"
              borderBottom="1px solid"
              borderColor="rgba(15, 23, 42, 0.08)"
            >
              <Box>
                <Text fontWeight="800" color="gray.900">
                  Latest comments
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Click a comment to reply
                </Text>
              </Box>
              <Badge colorScheme="pink" borderRadius="full" px={3} py={1}>
                {comments.length} loaded
              </Badge>
            </Flex>

            <Box maxH={{ base: 'unset', lg: 'calc(100vh - 180px)' }} overflowY="auto" p={5}>
              <VStack align="stretch" spacing={4}>
                {comments.map((comment) => (
                  <Card
                    key={comment.id}
                    border="1px solid"
                    borderColor="rgba(15, 23, 42, 0.08)"
                    borderRadius="24px"
                    boxShadow="none"
                    _hover={{ borderColor: '#F3B2D1' }}
                    onClick={() => {
                      setSelectedComment(comment);
                      onOpen();
                    }}
                    cursor="pointer"
                  >
                    <CardBody>
                      <VStack align="stretch" spacing={3}>
                        {comment.parent_comment ? (
                          <Box
                            bg="#F8FAFC"
                            borderRadius="16px"
                            px={4}
                            py={3}
                            border="1px solid"
                            borderColor="rgba(15, 23, 42, 0.06)"
                          >
                            <Text fontSize="xs" color="gray.500" mb={1}>
                              Replying to
                            </Text>
                            <Text fontSize="sm" color="gray.700">
                              <b>{comment.parent_comment.sender_username}</b> {comment.parent_comment.text}
                            </Text>
                          </Box>
                        ) : null}

                        <Flex justify="space-between" align="flex-start" gap={4}>
                          <Box>
                            <Text fontWeight="700" color="gray.900">
                              {comment.sender_username}
                            </Text>
                            <Text fontSize="sm" color="gray.600" mt={1}>
                              {comment.text}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                            {formatInstagramConversationTime(comment.timestamp)}
                          </Text>
                        </Flex>

                        <HStack spacing={2}>
                          <Badge colorScheme="pink" borderRadius="full" px={2.5} py={0.5}>
                            Post {comment.post_id}
                          </Badge>
                          {comment.replies?.length ? (
                            <Badge borderRadius="full" px={2.5} py={0.5}>
                              {comment.replies.length} replies
                            </Badge>
                          ) : null}
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}

                {loading ? (
                  <Flex justify="center" py={6}>
                    <Spinner color={igAccent} />
                  </Flex>
                ) : null}
                <Box ref={loaderRef} />
              </VStack>
            </Box>
          </Box>
        </Grid>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="28px">
          <ModalHeader>Reply to comment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedComment ? (
              <VStack align="stretch" spacing={4}>
                <Box
                  border="1px solid"
                  borderColor="rgba(15, 23, 42, 0.08)"
                  borderRadius="20px"
                  px={4}
                  py={3}
                  bg="gray.50"
                >
                  <Text fontWeight="700" color="gray.900">
                    {selectedComment.sender_username}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {selectedComment.text}
                  </Text>
                </Box>
                <Textarea
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(event) => setReplyText(event.target.value)}
                  minH="140px"
                  borderRadius="20px"
                />
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="pink" onClick={sendReply}>
              Send reply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InstagramCommentsPage;
